import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CotinSpin } from '@cotin/biblioteca-componentes-react';
import { useAuth } from '@/core/hooks/useAuth';
import {
    buildAuthLoginHref,
    getLoginIdFromUrl,
    isAuthLocalCompleted,
} from '@/core/configs/auth/auth.config';
import { hasValidLoginAD } from '@/core/configs/auth/auth.utils';
import {
    checkMaintenanceStatus,
    getMaintenancePath,
    shouldSkipMaintenanceCheck,
} from '@/services/maintenance.service';
import { env } from '@/environments';
import '@/styles/shared/_loading-overlay.scss';

const AuthGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { getSessionUser, logout, usuarioSessao } = useAuth();
    const [authorized, setAuthorized] = useState(false);
    const sessionRunIdRef = useRef(0);
    const sessionEstablishedRef = useRef(false);

    useEffect(() => {
        const loginId = getLoginIdFromUrl();
        if (loginId && !location.pathname.startsWith('/auth/login')) {
            navigate(buildAuthLoginHref(loginId), { replace: true });
            return;
        }

        // Local: cookie antigo (AuthLocal.Login) não libera rotas — exige formulário nesta sessão.
        if (
            env.authLocalEnabled === true &&
            !isAuthLocalCompleted() &&
            !loginId &&
            !location.pathname.startsWith('/auth/login')
        ) {
            navigate('/auth/login', { replace: true });
            return;
        }

        if (sessionEstablishedRef.current) {
            return;
        }

        const runId = ++sessionRunIdRef.current;
        let cancelled = false;
        const isActiveRun = () => !cancelled && runId === sessionRunIdRef.current;

        const authorize = () => {
            if (!isActiveRun()) {
                return;
            }
            sessionEstablishedRef.current = true;
            setAuthorized(true);
        };

        const redirectToLoginOrLogout = async () => {
            const id = getLoginIdFromUrl();
            if (id) {
                navigate(buildAuthLoginHref(id), { replace: true });
                return;
            }
            if (env.authLocalEnabled === true) {
                navigate('/auth/login', { replace: true });
                return;
            }
            await logout();
        };

        const runInitialGuard = async () => {
            try {
                const usuario =
                    usuarioSessao && hasValidLoginAD(usuarioSessao)
                        ? usuarioSessao
                        : await getSessionUser({ logoutIfMissing: false });

                if (!isActiveRun()) {
                    return;
                }

                if (!hasValidLoginAD(usuario)) {
                    await redirectToLoginOrLogout();
                    return;
                }

                const pathAtCheck = location.pathname + location.search;
                if (shouldSkipMaintenanceCheck(pathAtCheck)) {
                    authorize();
                    return;
                }

                const snapshot = await checkMaintenanceStatus();
                if (!isActiveRun()) {
                    return;
                }

                if (snapshot.isMaintenanceMode) {
                    navigate(getMaintenancePath(), { replace: true });
                    return;
                }

                authorize();
            } catch {
                if (!isActiveRun()) {
                    return;
                }
                await redirectToLoginOrLogout();
            }
        };

        void runInitialGuard();

        return () => {
            cancelled = true;
        };
    }, [getSessionUser, logout, location.pathname, location.search, navigate, usuarioSessao]);

    if (!authorized) {
        return (
            <div className="loading-spinner-overlay" role="status" aria-live="polite">
                <CotinSpin id="auth-gate-spin" variation="simple" size="medium" text="Carregando..." />
            </div>
        );
    }

    return <>{children}</>;
};

export default AuthGate;
